import { callLambdaFunction } from '../aiClient';

const CHAT_COMPLETION_ENDPOINT = import.meta.env?.VITE_AWS_LAMBDA_CHAT_COMPLETION_URL;

/**
 * Get a chat completion from the AI provider via Lambda
 * @param {string} provider - AI provider (e.g., 'OPEN_AI', 'ANTHROPIC', 'GEMINI')
 * @param {string} model - Model name
 * @param {Array} messages - Messages array
 * @param {Object} options - Optional parameters
 * @returns {Promise<Object>} Full SDK response object
 */
export async function getChatCompletion(provider, model, messages, options = {}) {
  if (!CHAT_COMPLETION_ENDPOINT) {
    console.warn('AI service not configured: VITE_AWS_LAMBDA_CHAT_COMPLETION_URL is not set');
    return {
      choices: [{
        message: {
          content: 'AI service is not configured. Please set up the AWS Lambda endpoint to enable AI features.',
          role: 'assistant'
        }
      }],
      usage: { total_tokens: 0 },
      _fallback: true
    };
  }

  const { stream, ...parameters } = options;

  const payload = {
    provider: provider || 'OPEN_AI',
    model: model || 'gpt-4o',
    messages: messages || [],
    stream: stream || false,
    parameters
  };

  try {
    const response = await callLambdaFunction(CHAT_COMPLETION_ENDPOINT, payload);
    return response;
  } catch (error) {
    console.error('Chat completion error:', error?.message);
    throw error;
  }
}

/**
 * Get a streaming chat completion from the AI provider via Lambda
 */
export async function getStreamingChatCompletion(
  provider,
  model,
  messages,
  onChunk,
  onComplete,
  onError,
  options = {}
) {
  try {
    const response = await getChatCompletion(provider, model, messages, {
      ...options,
      stream: true
    });
    onComplete?.(response);
    return response;
  } catch (error) {
    onError?.(error);
    throw error;
  }
}

export default getChatCompletion;
